import React, { memo, useContext } from 'react'
import { agnostify } from '@sainsburys-tech/boltui-utils'
import { ThemeContext } from 'styled-components'
import * as Styled from './C006.styles'
import { C006Props } from './types'
import { createGolDataAttributes } from '../../../helpers/tracking'

/**
 * - C006 is a heading component which is used by authors to render H2s, with or without a link.
 * - The heading can be horizontally aligned based on the textAlignment prop.
 * */

const C006 = ({
  link = {
    url: '',
    title: '',
    target: '',
    copy: ''
  },
  backgroundColor,
  hasMargin = false,
  heading,
  linkColor,
  onLinkClick,
  textColor,
  textAlignment = 'left'
}: C006Props): React.ReactElement => {
  const theme = useContext(ThemeContext)
  const isSainsburys = theme?.channel === 'sainsburys'
  const hasLink = !!link?.url

  const getGOLDataAttributes = (action?: string) =>
    createGolDataAttributes('isolated', 'C006 - NA', link?.title, link?.url, heading, action)

  const addGolTracking = (event) => {
    const dataAttributes = getGOLDataAttributes()
    Object.keys(dataAttributes).forEach((attribute) =>
      event.currentTarget.setAttribute(attribute, dataAttributes[attribute])
    )
  }

  const ctaClick = (event) => {
    if (onLinkClick) {
      onLinkClick(event, agnostify(link?.url))
    }
    addGolTracking(event)
  }

  return (
    <header>
      <Styled.Container
        {...getGOLDataAttributes('Load')}
        testid='c006-container'
        backgroundColor={backgroundColor}
        hasMargin={hasMargin}
        gap='none'
        cols={{ sm: 1, md: hasLink ? 2 : 1 }}
      >
        <Styled.Heading
          data-test='c006-heading'
          textColor={textColor}
          textAlignment={textAlignment}
          className={isSainsburys ? 'ln-u-display-2' : undefined} // this is to mitigate the large font sizes on Luna, should be reverted after they've addressed
        >
          {heading}
        </Styled.Heading>
        {link?.url && link?.copy && (
          <Styled.LinkContainer linkColor={linkColor}>
            <a
              data-test='c006-cta'
              target={link?.target}
              title={link?.title}
              href={agnostify(link?.url)}
              onClick={(event) => ctaClick(event)}
            >
              {link?.copy}
              <Styled.Chevron data-test='c006-cta-chevron' dir='right' linkColor={linkColor} />
            </a>
          </Styled.LinkContainer>
        )}
      </Styled.Container>
    </header>
  )
}

export default memo(C006)
